import dayjs from "dayjs";
import { z, RefinementCtx } from "zod";

import { PropertyStructureEnum } from "@/api/generated";

import {
  createRequiredMessage,
  createSelectRequiredMessage,
  createMaxLengthMessage,
  HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
  INVALID_DATE_MESSAGE,
  PRICE_LIMIT_MESSAGE,
  NON_NEGATIVE_MESSAGE,
  INTEGER_FORMAT_MESSAGE,
  MONTH_MULTIPLIER_RANGE_MESSAGE,
} from "../message";
import {
  HALF_WIDTH_NUMBER_REGEX,
  HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX,
  isWithinIntLimit,
  isNonNegative,
  isInteger,
  isWithinMonthMultiplierLimit,
} from "../patterns";

import { getPaymentPeriodError, PAYMENT_PERIOD_RANGE_MESSAGE } from "./paymentPeriod";

// 日付の基本的な範囲チェック（1-31）
const isValidDayRange = (val: string): boolean => {
  const day = Number(val);
  return day >= 1 && day <= 31;
};

// 共通の日付妥当性チェック関数
const checkDateValidity = (yearMonth: unknown, day: string | number): { isValid: boolean; maxDay?: number } => {
  const yearMonthDate = dayjs(yearMonth as never);
  const dayNum = typeof day === "string" ? Number(day) : day;

  if (!yearMonthDate.isValid() || isNaN(dayNum)) {
    return { isValid: false };
  }

  const maxDay = yearMonthDate.daysInMonth();
  const isValid = dayNum >= 1 && dayNum <= maxDay;

  return { isValid, maxDay };
};

// 日付バリデーション
export const validateDateField = (
  yearMonth: string | Date | null | undefined,
  day: string | null | undefined,
  dayFieldPath: string[],
  ctx: RefinementCtx
) => {
  if (!yearMonth || yearMonth === null || !day || day === "") return;

  const result = checkDateValidity(yearMonth, day);

  if (!result.isValid && result.maxDay) {
    const yearMonthDate = dayjs(yearMonth as never);
    ctx.addIssue({
      code: "custom",
      path: dayFieldPath,
      message: `${yearMonthDate.format("YYYY年MM月")}の有効な日にち（1〜${result.maxDay}日）を入力してください。`,
    });
  }
};

// 厳密な日付バリデーション関数（送信時用）
export const validateStrictDate = (yearMonth: string | Date, day: string): { isValid: boolean; message?: string } => {
  if (!yearMonth || !day) {
    return { isValid: true };
  }

  const yearMonthDate = dayjs(yearMonth);

  if (!yearMonthDate.isValid()) {
    return { isValid: false, message: "年月が不正です。" };
  }

  const result = checkDateValidity(yearMonth, day);

  if (!result.isValid) {
    return {
      isValid: false,
      message: `${yearMonthDate.format("YYYY年MM月")}の有効な日にち（1〜${result.maxDay}日）を入力してください。`,
    };
  }

  return { isValid: true };
};

// Step1の基本スキーマ（registrationから移動）
export const basicInfoSchema = z.object({
  name: z.string().min(1, createRequiredMessage()),
  room_number: z
    .string()
    .min(1, createRequiredMessage())
    .max(5, createMaxLengthMessage(5))
    .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE),
  floor: z
    .string()
    .min(1, createRequiredMessage("所在階"))
    .max(3, createMaxLengthMessage(3))
    .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE),
  prefecture: z
    .string()
    .nullable()
    .refine((val) => val, {
      message: createSelectRequiredMessage(),
    }),
  city: z
    .string()
    .nullable()
    .refine((val) => val, {
      message: createSelectRequiredMessage(),
    }),
  town: z
    .string()
    .nullable()
    .refine((val) => val, {
      message: createSelectRequiredMessage(),
    }),
  block: z.string().optional().nullable(),
  railway: z
    .string()
    .nullable()
    .refine((val) => val, {
      message: createSelectRequiredMessage(),
    }),
  station: z
    .string()
    .nullable()
    .refine((val) => val, {
      message: createSelectRequiredMessage(),
    }),
  walk: z
    .string()
    .min(1, createRequiredMessage("駅徒歩"))
    .max(3, createMaxLengthMessage(3))
    .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE),
  built_at: z.string().min(1, createSelectRequiredMessage("築年")),
  occupied_area_m2: z
    .string()
    .min(1, createRequiredMessage("専有面積"))
    .regex(HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE),
  layout: z.string().optional(),
  structure: z.nativeEnum(PropertyStructureEnum),
  management_type: z.string().min(1, createSelectRequiredMessage("管理形態")),
  sales_status: z
    .string()
    .nullable()
    .refine((val) => val, {
      message: createSelectRequiredMessage(),
    }),
  property_image: z.preprocess((val) => {
    // FileオブジェクトはJSON.stringifyで{}になるため、復元時に無効な値を空文字に変換
    if (val instanceof File) return val;
    if (typeof val === "string") return val;
    return "";
  }, z.union([z.string(), z.instanceof(File)]).optional()),
  asking_price: z.string().refine(isInteger, { message: INTEGER_FORMAT_MESSAGE }).refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE }).refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }).optional(),
});

// Step2: 価格・ローン情報のフォームスキーマ（registrationから移動）
export const priceLoanSchema = z.object({
  bought_at: z.union([z.string(), z.date(), z.null()]).refine((val) => val !== null && val !== "", {
    message: createSelectRequiredMessage("年月日"),
  }),
  bought_at_day: z
    .string()
    .min(1, createRequiredMessage())
    .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE)
    .refine(isValidDayRange, {
      message: INVALID_DATE_MESSAGE,
    }),

  // 価格情報
  total_price_of_property: z
    .string()
    .min(1, createRequiredMessage())
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
  priceof_tax: z
    .string()
    .min(1, createRequiredMessage())
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
  priceof_building: z
    .string()
    .min(1, createRequiredMessage())
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
  priceof_land: z
    .string()
    .min(1, createRequiredMessage())
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),

  // ローン情報
  loan_amount: z
    .string()
    .min(1, createRequiredMessage())
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
  loan_interest_rate_percentage: z
    .string()
    .max(5, createMaxLengthMessage(5))
    .refine((val) => !val || HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(val), HALF_WIDTH_NUMBER_FORMAT_MESSAGE)
    .optional(),
  payment_starts_at: z.union([z.string(), z.date(), z.null()]).refine((val) => val !== null && val !== "", {
    message: createSelectRequiredMessage("年月日"),
  }),
  payment_period: z
    .string()
    .min(1, createRequiredMessage())
    .refine((value) => !getPaymentPeriodError(value, 0), { message: PAYMENT_PERIOD_RANGE_MESSAGE }),
});

// Step3: 月間収支情報（registrationから移動）
export const cashflowSchema = z.object({
  // 収入
  monthly_fee: z
    .string()
    .min(1, createRequiredMessage())
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
  monthly_management_fee: z
    .string()
    .min(1, createRequiredMessage())
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
  other_monthly_income: z
    .string()
    .optional()
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),

  // 契約時収入（家賃の月数で入力する）
  type_of_the_income_of_the_beginning_of_contract: z.string().optional(),
  security_deposit: z
    .string()
    .max(5, createMaxLengthMessage(5))
    .optional()
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .refine((val) => !val || isWithinMonthMultiplierLimit(val), { message: MONTH_MULTIPLIER_RANGE_MESSAGE }),
  key_money: z
    .string()
    .max(5, createMaxLengthMessage(5))
    .optional()
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .refine((val) => !val || isWithinMonthMultiplierLimit(val), { message: MONTH_MULTIPLIER_RANGE_MESSAGE }),

  // 支出
  building_management_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
  repair_fund_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
  management_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
  misc_cost: z
    .string()
    .optional()
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),

  // 手数料（家賃の月数で入力する）
  outsourcing_fee_rate_of_new_lease_contract: z
    .string()
    .max(5, createMaxLengthMessage(5))
    .refine((val) => !val || HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(val), {
      message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
    })
    .refine((val) => !val || isWithinMonthMultiplierLimit(val), { message: MONTH_MULTIPLIER_RANGE_MESSAGE })
    .optional(),
  outsourcing_fee_rate_of_updating_lease_contract: z
    .string()
    .max(5, createMaxLengthMessage(5))
    .refine((val) => !val || HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(val), {
      message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
    })
    .refine((val) => !val || isWithinMonthMultiplierLimit(val), { message: MONTH_MULTIPLIER_RANGE_MESSAGE })
    .optional(),

  // 契約期間
  starts_at: z.union([z.string(), z.date(), z.null()]).refine((val) => val !== null && val !== "", {
    message: createRequiredMessage("年月日"),
  }),
  starts_at_day: z
    .string()
    .min(1, createRequiredMessage())
    .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE)
    .refine(isValidDayRange, {
      message: INVALID_DATE_MESSAGE,
    }),
  ends_at: z.union([z.string(), z.date(), z.null()]).refine((val) => val !== null && val !== "", {
    message: createRequiredMessage("年月日"),
  }),
  ends_at_day: z
    .string()
    .min(1, createRequiredMessage())
    .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE)
    .refine(isValidDayRange, {
      message: INVALID_DATE_MESSAGE,
    }),

  // 希望売却価格
  asking_price: z
    .string()
    .optional()
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),
});

// Step4: 建物全体の月間経費（registrationから移動）
export const buildingCostSchema = z.object({
  management_company_commission: z
    .string()
    .optional()
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),
  shared_utility_expenses: z
    .string()
    .optional()
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),
  misc_building_cost: z
    .string()
    .optional()
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),
});
